import { clientConfig } from '@/config/clientConfig';
import { PageHeader } from '@/shared/components/PageHeader';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { AlertCircle, Check, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useCreateUser, useJobTitles, useLineManagers } from '../hooks/useUsers';

interface AddUserViewProps {
  onBack: () => void;
  onUserCreated: (userId: string) => void;
}

type Step = 1 | 2 | 3;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
import {
  ALLOWED_NAME_PATTERN,
  ALLOWED_TEXT_PATTERN,
  NAME_VALIDATION_MSG,
  PHONE_PATTERN,
  PHONE_VALIDATION_MSG,
  TEXT_VALIDATION_MSG,
} from '@/shared/lib/validation';

export function AddUserView({ onBack: _onBack, onUserCreated }: AddUserViewProps) {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const createUser = useCreateUser();
  const isCompleting = createUser.isPending;
  const { data: jobTitlesData } = useJobTitles();
  const { data: lineManagersData } = useLineManagers();

  // Step 1: Personal Details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  // Validation state
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [addressTouched, setAddressTouched] = useState(false);

  // Step 2: Job/Role Details
  const [jobTitle, setJobTitle] = useState('');
  const [contracts, setContracts] = useState<string[]>([]);
  const [lineManager, setLineManager] = useState('');
  const [accountType, setAccountType] = useState('');
  const [isMobileUser, setIsMobileUser] = useState(false);

  const isFirstNameValid =
    firstName.length > 0 && firstName.length <= 50 && ALLOWED_NAME_PATTERN.test(firstName);
  const isLastNameValid =
    lastName.length > 0 && lastName.length <= 50 && ALLOWED_NAME_PATTERN.test(lastName);
  const isEmailValid = EMAIL_REGEX.test(email);
  const isPhoneValid = phone.length > 0 && PHONE_PATTERN.test(phone);
  const isAddressValid = address.length === 0 || ALLOWED_TEXT_PATTERN.test(address);

  const showFirstNameError = firstNameTouched && firstName.length > 0 && !isFirstNameValid;
  const showLastNameError = lastNameTouched && lastName.length > 0 && !isLastNameValid;
  const showEmailError = emailTouched && email.length > 0 && !isEmailValid;
  const showPhoneError = phoneTouched && phone.length > 0 && !isPhoneValid;
  const showAddressError = addressTouched && address.length > 0 && !isAddressValid;
  const canProceedStep1 =
    isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid && isAddressValid;
  const canProceedStep2 = jobTitle && lineManager && accountType;

  // Resolve display names for confirmation step
  const lineManagerName = lineManagersData?.values?.find(
    (lm: { id: number; name: string }) => String(lm.id) === lineManager,
  )?.name;
  const accountTypeLabel =
    accountType === 'normal'
      ? 'Normal Account'
      : accountType === 'third-party'
        ? 'Third Party'
        : accountType === 'contractor'
          ? 'Contractor'
          : accountType;

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((currentStep + 1) as Step);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  const handleComplete = () => {
    const accountTypeMap: Record<string, number> = {
      normal: 1,
      'third-party': 2,
      contractor: 3,
    };

    createUser.mutate(
      {
        firstName,
        secondName: lastName,
        workEmail: email || undefined,
        personalPhone: phone || undefined,
        addressLine1: address || undefined,
        jobTitle: jobTitle || undefined,
        lineManagerId: lineManager ? parseInt(lineManager, 10) : undefined,
        accountType: accountTypeMap[accountType] ?? 1,
        isMobileUser,
        addUser: true,
      },
      {
        onSuccess: (data) => {
          onUserCreated(String(data.userId));
        },
        onError: (error: any) => {
          showErrorToast(
            'Failed to Create User',
            error?.message || 'An unexpected error occurred. Please try again.',
          );
        },
      },
    );
  };

  const toggleContract = (contract: string) => {
    if (contracts.includes(contract)) {
      setContracts(contracts.filter((c) => c !== contract));
    } else {
      setContracts([...contracts, contract]);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page Header */}
      <PageHeader
        title="Add New User"
        subtitle="Create a new user account for your organisation"
        icon={<UserPlus className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        breadcrumbs={[
          { label: 'Users', href: '/users' },
          { label: 'Add User', current: true },
        ]}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                      step < currentStep
                        ? 'bg-green-600 text-white'
                        : step === currentStep
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {step < currentStep ? <Check className="w-6 h-6" /> : step}
                  </div>
                  <span
                    className={`mt-2 text-sm font-medium ${
                      step === currentStep
                        ? 'text-orange-600 dark:text-orange-400'
                        : step < currentStep
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {step === 1 ? 'Personal Details' : step === 2 ? 'Job Details' : 'Confirmation'}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`w-32 h-1 mx-4 transition-all ${
                      step < currentStep ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-800'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto bg-card border border-border rounded-lg p-8">
          {/* Step 1: Personal Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-foreground mb-4">Personal Details</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Enter the basic personal information for the new user
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => setFirstNameTouched(true)}
                    maxLength={50}
                    placeholder="Enter first name"
                    className={`bg-background ${showFirstNameError ? 'border-red-500 focus-visible:ring-red-500' : 'border-border'}`}
                  />
                  {showFirstNameError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {NAME_VALIDATION_MSG}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => setLastNameTouched(true)}
                    maxLength={50}
                    placeholder="Enter last name"
                    className={`bg-background ${showLastNameError ? 'border-red-500 focus-visible:ring-red-500' : 'border-border'}`}
                  />
                  {showLastNameError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {NAME_VALIDATION_MSG}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="user@example.com"
                  className={`bg-background ${showEmailError ? 'border-red-500 focus-visible:ring-red-500' : 'border-border'}`}
                />
                {showEmailError && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Please enter a valid email address
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  placeholder="07911123456"
                  className={`bg-background ${showPhoneError ? 'border-red-500 focus-visible:ring-red-500' : 'border-border'}`}
                />
                {showPhoneError && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {PHONE_VALIDATION_MSG}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Address</label>
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onBlur={() => setAddressTouched(true)}
                  placeholder="Enter full address"
                  className={`bg-background ${showAddressError ? 'border-red-500 focus-visible:ring-red-500' : 'border-border'}`}
                />
                {showAddressError && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {TEXT_VALIDATION_MSG}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Job/Role Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-foreground mb-4">Job & Role Details</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Define the user's role, responsibilities, and access level
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <Select value={jobTitle} onValueChange={setJobTitle}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select job title" />
                  </SelectTrigger>
                  <SelectContent>
                    {(jobTitlesData?.values ?? []).map((jt: { id: number; name: string }) => (
                      <SelectItem key={jt.id} value={jt.name}>
                        {jt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Line Manager <span className="text-red-500">*</span>
                </label>
                <Select value={lineManager} onValueChange={setLineManager}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select line manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {(lineManagersData?.values ?? []).map((lm: { id: number; name: string }) => (
                      <SelectItem key={lm.id} value={String(lm.id)}>
                        {lm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Account Type <span className="text-red-500">*</span>
                </label>
                <Select value={accountType} onValueChange={setAccountType}>
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal Account</SelectItem>
                    {clientConfig.features.thirdPartyAccounts && (
                      <>
                        <SelectItem value="third-party">Third Party</SelectItem>
                        <SelectItem value="contractor">Contractor</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3 p-4 bg-muted/30 border border-border rounded-lg">
                <input
                  type="checkbox"
                  id="mobileUser"
                  checked={isMobileUser}
                  onChange={(e) => setIsMobileUser(e.target.checked)}
                  className="w-5 h-5 rounded border-border text-orange-600 focus:ring-orange-500"
                />
                <div>
                  <label
                    htmlFor="mobileUser"
                    className="text-sm font-medium text-foreground cursor-pointer"
                  >
                    Mobile User
                  </label>
                  <p className="text-xs text-muted-foreground">
                    This user will have access to the mobile application
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-foreground mb-4">Confirm Details</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Please review the information before creating the user account
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {firstName} {lastName}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2 text-foreground font-medium">{email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone:</span>
                      <span className="ml-2 text-foreground font-medium">{phone}</span>
                    </div>
                    {address && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Address:</span>
                        <span className="ml-2 text-foreground font-medium">{address}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-muted/30 border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Job Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Job Title:</span>
                      <span className="ml-2 text-foreground font-medium">{jobTitle}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Line Manager:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {lineManagerName || lineManager}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Account Type:</span>
                      <span className="ml-2 text-foreground font-medium">{accountTypeLabel}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Mobile User:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {isMobileUser ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {contracts.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Contracts:</span>
                        <span className="ml-2 text-foreground font-medium">
                          {contracts.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isCompleting && (
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                      Creating user account...
                    </p>
                  </div>
                </div>
              )}

              {createUser.isError && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                      Failed to create user. Please try again.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handlePreviousStep}
                disabled={isCompleting}
                className="border-border"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
            {currentStep < 3 && (
              <Button
                className="ml-auto bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleNextStep}
                disabled={currentStep === 1 ? !canProceedStep1 : !canProceedStep2}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {currentStep === 3 && (
              <Button
                className="ml-auto bg-green-600 hover:bg-green-700 text-white"
                onClick={handleComplete}
                disabled={isCompleting}
              >
                <Check className="w-4 h-4 mr-2" />
                {isCompleting ? 'Creating...' : 'Create User'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
